// src/pages/Workflows/Editor/index.js — Tela 2: Editor de Workflow

import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';

import { useWorkflowEditor } from './hooks/useWorkflowEditor';
import { useWorkflowCanvas } from './hooks/useWorkflowCanvas';
import NodePalette from './components/NodePalette';
import WorkflowCanvas from './components/WorkflowCanvas';
import NodeConfigPanel from './components/NodeConfigPanel';

import '../Workflows.css';
import './Editor.css';

export default function WorkflowEditor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    nome, setNome,
    visibilidade, setVisibilidade,
    loading, saving, error,
    carregar, guardar,
  } = useWorkflowEditor(id);

  const {
    nodes, edges,
    onNodesChange, onEdgesChange,
    onConnect,
    isValidConnection,
    selectedNode, setSelectedNodeId,
    addNode,
    updateNodeConfig,
    deleteNode,
    inicializarComDefinicao,
    getDefinicao,
  } = useWorkflowCanvas();

  // Carregar definição ao montar (edição)
  useEffect(() => {
    if (id) {
      carregar().then(definicao => {
        if (definicao) inicializarComDefinicao(definicao);
      });
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGuardar = async () => {
    const definicao = getDefinicao();
    await guardar(definicao);
  };

  const handleCancelar = () => navigate('/workflows');

  const handleNodeClick = (node) => {
    setSelectedNodeId(node?.id || null);
  };

  if (loading) {
    return <div className="wf-editor-loading">A carregar workflow...</div>;
  }

  return (
    <ReactFlowProvider>
      <div className="wf-editor-page">
        {/* ── Header ───────────────────────────────────────────────── */}
        <div className="wf-editor-header">
          <input
            className="wf-editor-nome-input"
            value={nome}
            onChange={e => setNome(e.target.value)}
            placeholder="Nome do workflow"
          />

          <div className="wf-editor-visibility">
            <label>
              <input
                type="radio"
                name="visibilidade"
                value="private"
                checked={visibilidade === 'private'}
                onChange={() => setVisibilidade('private')}
              />
              <span>🔒 Privado</span>
            </label>
            <label>
              <input
                type="radio"
                name="visibilidade"
                value="shared"
                checked={visibilidade === 'shared'}
                onChange={() => setVisibilidade('shared')}
              />
              <span>🔗 Partilhado</span>
            </label>
            <label>
              <input
                type="radio"
                name="visibilidade"
                value="chat"
                checked={visibilidade === 'chat'}
                onChange={() => setVisibilidade('chat')}
              />
              <span>Chat</span>
            </label>
          </div>

          {error && <span className="wf-editor-error">{error}</span>}

          <div className="wf-editor-header-actions">
            <button className="wf-btn wf-btn-secondary" onClick={handleCancelar}>
              Cancelar
            </button>
            <button className="wf-btn wf-btn-primary" onClick={handleGuardar} disabled={saving}>
              {saving ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </div>

        {/* ── Corpo: 3 colunas ─────────────────────────────────────── */}
        <div className="wf-editor-body">
          {/* Coluna esquerda: Paleta */}
          <NodePalette onAddNode={addNode} />

          {/* Centro: Canvas */}
          <WorkflowCanvas
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            isValidConnection={isValidConnection}
            onNodeClick={handleNodeClick}
            onAddNode={addNode}
          />

          {/* Coluna direita: Config */}
          <NodeConfigPanel
            node={selectedNode}
            allNodes={nodes}
            onUpdateConfig={updateNodeConfig}
            onDeleteNode={deleteNode}
          />
        </div>
      </div>
    </ReactFlowProvider>
  );
}
